import React, { useContext, useState } from 'react';
import { ArrowBack as ArrowBackIcon, ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { styled } from '@mui/material';
import { Stepper as BaseStepper, Box, Button, Step, StepLabel, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { ConfirmationCancelDialog, Dialog } from 'components/Dialogs';
import DialogContext from 'context/DialogContext';
import { StepperDialogActions, StepperDialogContent } from './components';
import { StepperProps } from './Stepper.props';

const PREFIX = 'Stepper';

const classes = {
  root: `${PREFIX}-root`,
  title: `${PREFIX}-title`,
  closeButton: `${PREFIX}-closeButton`,
  stepContent: `${PREFIX}-stepContent`,
  actionButtonLabel: `${PREFIX}-actionButtonLabel`,
  arrowBackIcon: `${PREFIX}-arrowBackIcon`,
  arrowForwardIcon: `${PREFIX}-arrowForwardIcon`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.root}`]: {},

  [`& .${classes.title}`]: {
    paddingTop: theme.spacing(2),
  },

  [`& .${classes.closeButton}`]: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(2),
    color: '#000',
  },

  [`& .${classes.stepContent}`]: {
    paddingBottom: theme.spacing(2),
  },

  [`& .${classes.actionButtonLabel}`]: {
    fontSize: '0.8750em',
  },

  [`& .${classes.arrowBackIcon}`]: {
    fontSize: '1.4em',
    marginRight: theme.spacing(1),
  },

  [`& .${classes.arrowForwardIcon}`]: {
    fontSize: '1.4em',
    marginLeft: theme.spacing(1),
  },
}));

const Stepper = ({
  id,
  isVisible,
  title,
  stepperTitles = {},
  steps,
  onClose,
  onSubmit,
  confirmCancel,
  customFinishButtonTitle = 'Save and Finish',
}: StepperProps) => {
  const customId = `${id}-stepper`;

  const [{ isShowLoadingProgress }] = useStore() as unknown as UseStoreValues;
  const [activeStep, setActiveStep] = useState<number>(0);
  const [hasErrors, setHasErrors] = useState<boolean>(false);
  const { showDialog } = useContext(DialogContext);

  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === steps.length - 1;

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    const success = await onSubmit();
    setHasErrors(!success);

    if (success) {
      onClose();
    }
  };

  const handleClose = () => {
    if (confirmCancel) {
      showDialog({
        wrapper: ConfirmationCancelDialog,
        actions: [
          {
            label: 'Cancel Anyways',
            variant: 'contained',
            type: 'danger',
            callback: onClose,
          },
        ],
      });
    } else {
      onClose();
    }
  };

  return (
    <StyledDialog
      id={customId}
      title={`${stepperTitles[activeStep]}` || title}
      fullWidth
      maxWidth="md"
      onClose={onClose}
      open={isVisible}
      isLoading={isShowLoadingProgress}
      dialogProps={{
        disableBackdropClick: true,
        disableTitleClose: true,
      }}>
      <BaseStepper activeStep={activeStep} alternativeLabel>
        {steps.map(step => (
          <Step id={`step-${step.id}`} key={step.id}>
            <StepLabel>
              <Typography variant="h5" component="h5">
                {step.title}
              </Typography>
            </StepLabel>
          </Step>
        ))}
      </BaseStepper>
      <StepperDialogContent>
        <>
          <div className={classes.stepContent}>
            {hasErrors ? (
              <Alert variant="outlined" severity="error">
                Please review the information before continuing
              </Alert>
            ) : (
              ''
            )}
          </div>
          {steps[activeStep].component}
        </>
        <StepperDialogActions>
          <Button id={`${customId}-cancel-btn`} color="primary" onClick={handleClose} disabled={isShowLoadingProgress}>
            <span className={classes.actionButtonLabel}>Cancel</span>
          </Button>
          {!isFirstStep ? (
            <Button
              id={`${customId}-prev-btn`}
              color="primary"
              variant="outlined"
              disabled={activeStep === 0 || isShowLoadingProgress}
              onClick={handleBack}>
              <Box display="flex" alignItems="center">
                <ArrowBackIcon className={classes.arrowBackIcon} />
                <span className={classes.actionButtonLabel}>Prev</span>
              </Box>
            </Button>
          ) : (
            ''
          )}
          <Button
            id={`${customId}-continue-btn`}
            variant="contained"
            color="secondary"
            disabled={!steps[activeStep].isValid || isShowLoadingProgress}
            onClick={!isLastStep ? handleNext : handleSubmit}>
            <Box display="flex" alignItems="center">
              <span className={classes.actionButtonLabel}>{isLastStep ? customFinishButtonTitle : 'Continue'}</span>
              {!isLastStep ? <ArrowForwardIcon className={classes.arrowForwardIcon} /> : ''}
            </Box>
          </Button>
        </StepperDialogActions>
      </StepperDialogContent>
    </StyledDialog>
  );
};

export default Stepper;
